import { motion } from "framer-motion";
import { useLoanCalculator } from "@/hooks/useLoanCalculator";
import { LoanBenefitsSection } from "@/components/loan/LoanBenefitsSection";
import { ProcessStepsCards } from "@/components/loan/ProcessStepsCards";
import { LoanApplicationForm } from "@/components/loan/LoanApplicationForm";
import { FAQSection } from "@/components/loan/FAQSection";
import { BenefitsSection } from "@/components/loan/BenefitsSection";
import { useLoanFormState } from "@/hooks/useLoanFormState";
import { useApplicationSubmit } from "@/hooks/useApplicationSubmit";
import { StickyHeader } from "@/components/loan/StickyHeader";
import { ErrorAlert } from "@/components/loan/ErrorAlert";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useEffect, useState } from "react";
import { FormValues } from "@/types/loan";
import { useSearchParams, useLocation } from "react-router-dom";
import { LoanPurpose } from "@/services/api/types";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const KilpailutaPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { form, loanAmount, setLoanAmount, loanTerm, setLoanTerm, loanType } = useLoanFormState();
  
  // Käytämme localLoanType:a sisäisesti
  const [localLoanType, setLocalLoanType] = useState(loanType || "kulutusluotto");
  const { isSubmitting, submitError, handleSubmit } = useApplicationSubmit();
  const isMobile = useIsMobile();
  const { trackPageView, trackLoanApplicationStep } = useAnalytics();
  const seoData = getEnhancedSeoDataForRoute("/kilpailuta");
  
  const { monthlyPayment, totalPaid, interestAndFees, interestRate, monthlyFee, setupFee } = useLoanCalculator(loanAmount, loanTerm, localLoanType);

  useEffect(() => {
    const amountParam = searchParams.get("amount");
    const termParam = searchParams.get("term");
    const purposeParam = searchParams.get("purpose");
    const typeParam = searchParams.get("type");
    
    if (amountParam) {
      const parsedAmount = parseInt(amountParam, 10);
      if (!isNaN(parsedAmount) && parsedAmount >= 1000 && parsedAmount <= 100000) {
        setLoanAmount(parsedAmount);
        form.setValue("loanAmount", parsedAmount);
      }
    }
    
    if (termParam) {
      const parsedTerm = parseInt(termParam, 10);
      if (!isNaN(parsedTerm) && parsedTerm >= 1 && parsedTerm <= 25) {
        setLoanTerm(parsedTerm);
        form.setValue("loanTerm", parsedTerm);
      }
    }
    
    if (purposeParam) {
      const isValidPurpose = Object.values(LoanPurpose).includes(purposeParam as LoanPurpose);
      if (isValidPurpose) {
        form.setValue("loanPurpose", purposeParam as LoanPurpose);
      }
    }
    
    // Käsittelemme loan type -parametrin vaikka UI-komponenttia ei olekaan
    if (typeParam) {
      setLocalLoanType(typeParam);
      form.setValue("loanType", typeParam);
    }
  }, [searchParams, form, setLoanAmount, setLoanTerm]);

  // Päivitetään localLoanType aina kun loanType muuttuu
  useEffect(() => {
    if (loanType) {
      setLocalLoanType(loanType);
    }
  }, [loanType]);

  useEffect(() => {
    // Haetaan referrer-tiedot
    const referrer = document.referrer;
    const internalReferrer = sessionStorage.getItem('lastVisitedPage') || '';
    const sourceParam = searchParams.get("source") || "";
    
    // Selvitetään mistä käyttäjä tuli
    const referrerInfo = sourceParam || (internalReferrer.includes('/') ? internalReferrer : referrer);
    
    // Seurataan sivunkatselua tarkemmilla tiedoilla
    trackPageView('Kilpailuta Lainasi', '/kilpailuta', referrerInfo);
    
    // Seurataan hakusivun katselua parametreilla
    trackLoanApplicationStep('page_view', {
      loanType: localLoanType,
      loanAmount,
      loanTerm,
      referrer: referrerInfo,
      hasParameters: !!(searchParams.get("amount") || searchParams.get("term") || searchParams.get("type"))
    });
  }, []);

  useEffect(() => {
    trackLoanApplicationStep('loan_parameters_changed', {
      loanType: localLoanType,
      loanAmount,
      loanTerm
    });
  }, [loanAmount, loanTerm, localLoanType]);

  const handleSubmitWithTracking = async (data: FormValues) => {
    // Varmistetaan että loanType on asetettu lomakkeelle ennen lähetystä
    if (!data.loanType) {
      data.loanType = localLoanType;
      form.setValue("loanType", localLoanType);
    }
    
    // Haetaan referrer-tiedot
    const referrer = document.referrer;
    const internalReferrer = sessionStorage.getItem('lastVisitedPage') || '';
    const sourceParam = searchParams.get("source") || "";
    const referrerInfo = sourceParam || (internalReferrer.includes('/') ? internalReferrer : referrer);
    
    trackLoanApplicationStep('form_submission_attempt', {
      loanType: data.loanType,
      loanAmount: data.loanAmount,
      loanTerm: data.loanTerm,
      referrer: referrerInfo
    });
    
    await handleSubmit(data);
  };

  return (
    <div className="min-h-screen bg-white px-2 py-3 md:p-6">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/kilpailuta"
        schema={seoData.schema}
      />
    
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="md:mb-4 text-center md:text-left"
        >
          {!isMobile && (
            <h1 className="text-xl md:text-3xl font-bold mb-2 text-blue-700">Kilpailuta Lainasi</h1>
          )}
        </motion.div>
        
        <StickyHeader 
          loanAmount={loanAmount}
          loanTerm={loanTerm}
          monthlyPayment={monthlyPayment}
        />
        
        {submitError && <ErrorAlert errorMessage={submitError} />}
        
        {isMobile ? (
          // Mobile layout: form first, then instructions
          <>
            <div id="loan-application-form" className="w-full mb-4">
              <LoanApplicationForm 
                form={form}
                handleSubmit={handleSubmitWithTracking}
                loanAmount={loanAmount}
                setLoanAmount={setLoanAmount}
                loanTerm={loanTerm}
                setLoanTerm={setLoanTerm}
                monthlyPayment={monthlyPayment}
                totalPaid={totalPaid}
                interestAndFees={interestAndFees}
                interest={interestRate}
                isSubmitting={isSubmitting}
                setLoanType={setLocalLoanType}
              />
            </div>
            
            <ProcessStepsCards />
            <LoanBenefitsSection />
            <FAQSection />
            <BenefitsSection />
          </>
        ) : (
          // Desktop layout: two columns - instructions left, form right
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Left column: Instructions and benefits */}
              <div className="lg:order-1">
                <LoanBenefitsSection />
                <ProcessStepsCards />
                <FAQSection />
                <BenefitsSection />
              </div>
              
              {/* Right column: Application form */}
              <div className="lg:order-2">
                <div id="loan-application-form" className="lg:sticky lg:top-6">
                  <LoanApplicationForm 
                    form={form}
                    handleSubmit={handleSubmitWithTracking}
                    loanAmount={loanAmount}
                    setLoanAmount={setLoanAmount}
                    loanTerm={loanTerm}
                    setLoanTerm={setLoanTerm}
                    monthlyPayment={monthlyPayment}
                    totalPaid={totalPaid}
                    interestAndFees={interestAndFees}
                    interest={interestRate}
                    isSubmitting={isSubmitting}
                    setLoanType={setLocalLoanType}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KilpailutaPage;
